import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, PlayCircle } from 'lucide-react';

interface Step {
    title: string;
    description: string;
    imageUrl: string;
}

interface StepAnimatorProps {
    steps: Step[];
}

export const StepAnimator: React.FC<StepAnimatorProps> = ({ steps }) => {
    const [currentStep, setCurrentStep] = useState(0);

    const next = () => setCurrentStep((prev) => (prev + 1) % steps.length);
    const prev = () => setCurrentStep((prev) => (prev - 1 + steps.length) % steps.length);

    return (
        <div className="bg-slate-50 dark:bg-slate-800 rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-sm mt-4">
            <div className="relative aspect-video bg-slate-200 dark:bg-slate-900 flex items-center justify-center group">
                <AnimatePresence mode="wait">
                    <motion.img
                        key={currentStep}
                        src={steps[currentStep].imageUrl}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 1.05 }}
                        transition={{ duration: 0.3 }}
                        className="w-full h-full object-cover"
                        alt={steps[currentStep].title}
                        onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                    />
                    <div className="hidden absolute inset-0 flex flex-col items-center justify-center text-slate-400">
                        <PlayCircle size={48} className="mb-2 opacity-20" />
                        <span className="text-xs">[Visual Step Asset Missing]</span>
                    </div>
                </AnimatePresence>

                {/* Progress Indicators */}
                <div className="absolute top-4 left-4 flex gap-1">
                    {steps.map((_, idx) => (
                        <div
                            key={idx}
                            className={`h-1 rounded-full transition-all duration-300 ${idx === currentStep ? 'w-8 bg-blue-500' : 'w-2 bg-white/50'}`}
                        />
                    ))}
                </div>
            </div>

            <div className="p-4 bg-white dark:bg-slate-800">
                <div className="flex justify-between items-start mb-2">
                    <h4 className="font-bold text-slate-700 dark:text-slate-200 text-sm">
                        Step {currentStep + 1}: {steps[currentStep].title}
                    </h4>
                    <div className="flex gap-1">
                        <button
                            onClick={prev}
                            className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                        >
                            <ChevronLeft size={16} />
                        </button>
                        <button
                            onClick={next}
                            className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                    {steps[currentStep].description}
                </p>
            </div>
        </div>
    );
};
